import { useMemo } from "react";
import { useJson, nf, entityPath, card, sectionLabel, IdBadge, FRAMEWORK_TERMS } from "./shared";

// Frameworks: ATT&CK Enterprise / Mobile / ICS, ATLAS (AI), SPARTA (space) and D3FEND,
// each browsable as a matrix of tactics × techniques.

// "1 case study", "2 case studies".
const term = (n, word) => (n === 1 ? word.replace(/ies$/, "y").replace(/s$/, "") : word);

const SITE = {
  enterprise: "https://attack.mitre.org/", mobile: "https://attack.mitre.org/matrices/mobile/", ics: "https://attack.mitre.org/matrices/ics/",
  atlas: "https://atlas.mitre.org/", sparta: "https://sparta.aerospace.org/", d3fend: "https://d3fend.mitre.org/",
  emb3d: "https://emb3d.mitre.org/", capec: "https://capec.mitre.org/", engage: "https://engage.mitre.org/matrix/", fight: "https://fight.mitre.org/",
};

export function KbFrameworks({ meta, ui }) {
  const { B, fs } = ui;
  const list = Object.values(meta?.frameworks || {});
  return (
    <>
      <h2 style={{ fontSize: fs(22), fontWeight: 900, margin: "4px 0 6px", color: B.text }}>Frameworks</h2>
      <p style={{ color: B.textMuted, fontSize: fs(13), margin: "0 0 18px", maxWidth: "110ch", lineHeight: 1.6 }}>
        Each framework describes attacker behaviour for a different kind of target, in the same tactic → technique → mitigation structure. They're linked where their authors link them: ATLAS and CAPEC point back to the ATT&CK techniques they correspond to, D3FEND maps each defence to the ATT&CK techniques it counters, Engage maps each deception activity to the techniques it can engage, SPARTA countermeasures map to D3FEND, and CAPEC and EMB3D share CWE weaknesses with CVEs.
      </p>
      {!list.length && <div style={{ color: B.textDim, fontSize: fs(12) }}>Loading…</div>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 340px), 1fr))", gap: 14 }}>
        {list.map(f => {
          const terms = FRAMEWORK_TERMS[f.key] || FRAMEWORK_TERMS.enterprise;
          const links = [
            f.tactics.length && [`#/kb/frameworks/${f.key}`, "Matrix"],
            f.counts.techniques && [`#/kb/techniques?fw=${f.key}`, `${nf(f.counts.techniques)} ${term(f.counts.techniques, terms.techniques)}`],
            f.counts.mitigations && [`#/kb/mitigations?fw=${f.key}`, `${nf(f.counts.mitigations)} ${term(f.counts.mitigations, terms.mitigations)}`],
            f.counts.campaigns && [`#/kb/campaigns?fw=${f.key}`, `${nf(f.counts.campaigns)} ${term(f.counts.campaigns, terms.campaigns)}`],
          ].filter(Boolean);
          return (
            <div key={f.key} style={{ ...card(ui), padding: 18, display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: fs(16), fontWeight: 900, color: B.text }}>{f.label}</span>
                {f.version && <span style={{ fontSize: fs(11), color: B.textDim }}>v{f.version}</span>}
              </div>
              <div style={{ fontSize: fs(12), color: B.textMuted, lineHeight: 1.55 }}>{f.blurb}</div>
              <div style={{ display: "flex", gap: "6px 14px", flexWrap: "wrap", marginTop: "auto", paddingTop: 6 }}>
                {links.map(([href, label]) => <a key={href} href={href} style={{ fontSize: fs(12), color: B.accent, textDecoration: "none" }}>{label} →</a>)}
                <a href={SITE[f.key]} target="_blank" rel="noreferrer" style={{ fontSize: fs(12), color: B.textDim, textDecoration: "none" }}>{f.site} ↗</a>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

// One column per tactic in kill-chain order; each cell a technique with how much uses it.
export function KbFrameworkMatrix({ fw, meta, ui }) {
  const { B, fs } = ui;
  const f = meta?.frameworks?.[fw];
  // D3FEND's and Engage's techniques are defences (courses of action), so their matrices list mitigations.
  const { data, loading } = useJson(fw === "d3fend" || fw === "engage" ? "/api/kb/list/mitigations" : "/api/kb/list/techniques");
  const terms = FRAMEWORK_TERMS[fw] || FRAMEWORK_TERMS.enterprise;

  const { columns, threats } = useMemo(() => {
    const all = (data?.items || []).filter(s => (s.framework || "enterprise") === fw);
    const subs = new Map();
    for (const s of all) if (s.isSubtechnique && s.parentName) subs.set(s.parentName, (subs.get(s.parentName) || 0) + 1);
    const top = all.filter(s => !s.isSubtechnique).map(s => ({ ...s, subs: subs.get(s.name) || 0 }));
    return {
      columns: (f?.tactics || []).map(t => ({ t, items: top.filter(s => (s.tactics || []).includes(t.name)).sort((a, b) => a.name.localeCompare(b.name)) })).filter(c => c.items.length),
      threats: top.filter(s => s.spartaThreat).sort((a, b) => (a.attackId || "").localeCompare(b.attackId || "", undefined, { numeric: true })),
    };
  }, [data, f, fw]);

  // What "used" means differs: groups/software for ATT&CK, case studies for ATLAS,
  // countermeasures for SPARTA (it has no attribution data).
  const usage = (s) => {
    const c = s.counts || {};
    if (fw === "atlas") return c.campaigns ? `${nf(c.campaigns)} case ${c.campaigns === 1 ? "study" : "studies"}` : "";
    if (fw === "sparta") return c.mitigations ? `${nf(c.mitigations)} countermeasure${c.mitigations === 1 ? "" : "s"}` : "";
    if (fw === "emb3d") return c.mitigations ? `${nf(c.mitigations)} mitigation${c.mitigations === 1 ? "" : "s"}` : "";
    if (fw === "d3fend") return c.techniques ? `counters ${nf(c.techniques)}` : "";
    if (fw === "engage") return c.techniques ? `engages ${nf(c.techniques)}` : "";
    if (fw === "capec") return s.capec?.severity ? `${s.capec.severity} severity` : "";
    const n = (c.groups || 0) + (c.software || 0);
    return n ? `used by ${nf(n)}` : "";
  };

  if (!f) return <div style={{ color: B.textDim, fontSize: fs(13) }}>{meta ? "Unknown framework." : "Loading…"}</div>;
  return (
    <>
      <div style={{ fontSize: fs(12), color: B.textDim, marginBottom: 10 }}><a href="#/kb/frameworks" style={{ color: B.textDim, textDecoration: "none" }}>Frameworks</a> / {f.label}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", margin: "0 0 6px" }}>
        <h2 style={{ fontSize: fs(22), fontWeight: 900, margin: 0, color: B.text }}>{f.label}</h2>
        {f.version && <span style={{ fontSize: fs(12), color: B.textDim }}>v{f.version}</span>}
      </div>
      <p style={{ color: B.textMuted, fontSize: fs(13), margin: "0 0 8px", maxWidth: "110ch", lineHeight: 1.6 }}>{f.blurb}</p>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: fs(12), marginBottom: 16 }}>
        {f.counts.techniques > 0 && <a href={`#/kb/techniques?fw=${fw}`} style={{ color: B.accent, textDecoration: "none" }}>All {nf(f.counts.techniques)} {terms.techniques} as a list →</a>}
        {f.counts.mitigations > 0 && <a href={`#/kb/mitigations?fw=${fw}`} style={{ color: B.accent, textDecoration: "none" }}>{nf(f.counts.mitigations)} {term(f.counts.mitigations, terms.mitigations)} →</a>}
        {f.counts.campaigns > 0 && <a href={`#/kb/campaigns?fw=${fw}`} style={{ color: B.accent, textDecoration: "none" }}>{nf(f.counts.campaigns)} {term(f.counts.campaigns, terms.campaigns)} →</a>}
      </div>
      {loading && <div style={{ color: B.textDim, fontSize: fs(12) }}>Loading…</div>}
      <div style={{ overflowX: "auto", paddingBottom: 8 }}>
        <div style={{ display: "grid", gridAutoFlow: "column", gridAutoColumns: "minmax(170px, 1fr)", gap: 8, minWidth: columns.length * 178 }}>
          {columns.map(({ t, items }) => (
            <div key={t.name} style={{ minWidth: 0 }}>
              <div style={{ ...card(ui), padding: "8px 10px", marginBottom: 6, background: B.bgPanel }}>
                <div style={{ fontSize: fs(12), fontWeight: 800, color: B.text }}>{t.label}</div>
                <div style={{ fontSize: fs(10), color: B.textDim }}>{t.attackId ? `${t.attackId} · ` : ""}{items.length} {term(items.length, fw === "engage" ? "activities" : terms.techniques)}</div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                {items.map(s => (
                  <a key={s.id} href={entityPath(s)} title={s.snippet} style={{ ...card(ui), borderRadius: 8, padding: "7px 9px", textDecoration: "none", display: "block" }}>
                    <div style={{ fontSize: fs(12), color: B.text, fontWeight: 600, lineHeight: 1.35 }}>{s.name}</div>
                    <div style={{ fontSize: fs(10), color: B.textDim, marginTop: 3, display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <span style={{ fontFamily: "monospace" }}>{s.attackId}</span>
                      {s.subs > 0 && <span>· {s.subs} sub</span>}
                      {usage(s) && <span>· {usage(s)}</span>}
                    </div>
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      {threats.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <div style={{ ...sectionLabel(ui), marginBottom: 8 }}>Space-vehicle threats ({threats.length})</div>
          <p style={{ color: B.textMuted, fontSize: fs(12), margin: "0 0 10px", maxWidth: "110ch" }}>SPARTA's threat list: what could go wrong for a spacecraft, each linked to the techniques that realise it.</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 300px), 1fr))", gap: 8 }}>
            {threats.map(s => (
              <a key={s.id} href={entityPath(s)} style={{ ...card(ui), borderRadius: 8, padding: "9px 11px", textDecoration: "none", display: "flex", gap: 8, alignItems: "baseline" }}>
                <IdBadge id={s.attackId} ui={ui} />
                <span title={s.name} style={{ fontSize: fs(12), color: B.text, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{s.name}</span>
              </a>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
